/**
 * Merchant · Referral Program (Loop P4-referral)
 *
 * Reads GET /api/merchant/referral, shows the share link, referrals list, and stats.
 */
import { useEffect, useState } from "react";
import { Copy, Handshake, Loader2, Share2, Sparkles, Store, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function StatCard({ icon, label, value }) {
  return (
    <div className="pf-card p-4">
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
        {icon} <span>{label}</span>
      </div>
      <div className="text-2xl font-semibold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:   { c: "bg-slate-100 text-slate-700",    t: "Pending signup" },
    signed_up: { c: "bg-cyan-50 text-cyan-800",       t: "Signed up" },
    redeemed:  { c: "bg-emerald-50 text-emerald-800", t: "Redeemed" },
    cancelled: { c: "bg-red-50 text-red-800",         t: "Cancelled" },
  };
  const meta = map[status] || map.pending;
  return (
    <span
      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.c}`}
      data-testid={`referral-status-${status || "pending"}`}
    >
      {meta.t}
    </span>
  );
}

export default function ReferralProgram() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/merchant/referral")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load referral info"))
      .finally(() => setLoading(false));
  }, []);

  const copy = (text, msg) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(
      () => toast.success(msg || "Copied."),
      () => toast.error("Copy failed"),
    );
  };

  if (loading) {
    return (
      <div className="p-8 text-slate-500" data-testid="referral-loading">
        <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading referral program…
      </div>
    );
  }
  if (!data) return null;

  const { code, share_url: shareUrl, share_message: shareMsg, referrals, stats, rules } = data;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="referral-page">
      <div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 flex items-center gap-2" data-testid="referral-title">
          <Handshake className="w-7 h-7 text-burgundy-600" /> Refer &amp; earn
        </h1>
        <p className="text-slate-600 mt-1">
          Invite peer merchants. When they subscribe to Starter or Growth, you both
          get <strong>{rules.referrer_discount_pct}% off</strong> your next{" "}
          {rules.duration_months === 1 ? "month" : `${rules.duration_months} months`}.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<Users className="w-3.5 h-3.5" />}     label="Invited"   value={stats.total} />
        <StatCard icon={<Store className="w-3.5 h-3.5" />}     label="Signed up" value={stats.signed_up} />
        <StatCard icon={<Sparkles className="w-3.5 h-3.5" />}  label="Redeemed"  value={stats.redeemed} />
        <StatCard icon={<TrendingUp className="w-3.5 h-3.5" />} label="Discount" value={`${rules.referrer_discount_pct}%`} />
      </div>

      {/* Share block */}
      <div className="pf-card p-6" data-testid="referral-share-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-burgundy-100 to-cream-100 grid place-items-center shrink-0">
            <Share2 className="w-5 h-5 text-burgundy-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg font-semibold text-slate-900">Your referral link</h2>
            <p className="text-sm text-slate-600 mb-3">
              Share this in email, LinkedIn, or Slack. Both accounts unlock the discount
              when they subscribe.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-2 md:items-center">
              <label className="text-xs uppercase tracking-wide text-slate-500 md:pr-2">Code</label>
              <input
                readOnly
                value={code}
                data-testid="referral-code-input"
                onFocus={(e) => e.target.select()}
                onClick={() => copy(code, "Code copied.")}
                className="w-full md:w-40 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono tracking-wider bg-slate-50 cursor-pointer"
              />
              <button
                type="button"
                onClick={() => copy(code, "Code copied.")}
                className="pf-btn pf-btn-secondary shrink-0"
                data-testid="referral-copy-code"
              >
                <Copy className="w-4 h-4 mr-1.5" /> Copy
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-2 md:items-center mt-3">
              <label className="text-xs uppercase tracking-wide text-slate-500 md:pr-2">Link</label>
              <input
                readOnly
                value={shareUrl}
                data-testid="referral-link-input"
                onFocus={(e) => e.target.select()}
                onClick={() => copy(shareUrl, "Link copied.")}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-slate-50 truncate cursor-pointer"
              />
              <button
                type="button"
                onClick={() => copy(shareUrl, "Link copied.")}
                className="pf-btn pf-btn-primary shrink-0"
                data-testid="referral-copy-link"
              >
                <Copy className="w-4 h-4 mr-1.5" /> Copy
              </button>
            </div>
            {shareMsg && (
              <p className="text-xs text-slate-500 mt-3">
                <strong className="text-slate-700">Suggested message:</strong> {shareMsg}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Referred merchants */}
      <div className="pf-card p-6">
        <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">Your referrals</h2>
        {referrals.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center" data-testid="referrals-empty">
            No referrals yet. Share your link with a peer to earn your first
            {" "}{rules.referrer_discount_pct}% credit.
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="referrals-list">
            {referrals.map((r) => (
              <div key={r.id || r.invitee_merchant_id} className="py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center shrink-0">
                  <Store className="w-4 h-4 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {r.invitee_business_name || r.invitee_email || "—"}
                  </div>
                  <div className="text-xs text-slate-500">
                    Signed up {new Date(r.created_at).toLocaleDateString("en-CA")}
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
